// =============================================================================
// Admin Users Page — List, search, filter, create & manage admin users
// =============================================================================

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  Button,
  Badge,
  Input,
  Spinner,
} from '@tkpod/ui';
import { api } from '@/lib/api';
import type {
  AdminUserSummary,
  AdminRoleSummary,
  CreateAdminUserRequest,
} from '@tkpod/shared';

// ---------------------------------------------------------------------------
// API response types
// ---------------------------------------------------------------------------

interface UsersListResponse {
  data: AdminUserSummary[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface RolesListResponse {
  data: AdminRoleSummary[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/** Platform user search result (used for email lookup in create flow) */
interface PlatformUserSummary {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  status: string;
  isAdmin?: boolean;
  /**
   * Whether the user already has a credential (passwordHash set on User).
   * Passwordless users (e.g. provisioned via Shopify OAuth) must receive an
   * initial password when granted admin access or they cannot sign in.
   */
  hasPassword?: boolean;
}

interface PlatformUsersResponse {
  data: PlatformUserSummary[];
  pagination: { total: number; page: number; limit: number; totalPages: number };
}

// ---------------------------------------------------------------------------
// Password validation
// ---------------------------------------------------------------------------

/** Must match backend regex in CreateAdminUserDto and RegisterDto */
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{8,}$/;

function isPasswordValid(password: string): boolean {
  return PASSWORD_REGEX.test(password);
}

// ---------------------------------------------------------------------------
// Create form state
// ---------------------------------------------------------------------------

interface CreateFormState {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  forcePasswordChange: boolean;
  roleId: string;
  department: string;
  additionalPermissions: string;
  providerId: string;
}

const EMPTY_FORM: CreateFormState = {
  email: '',
  firstName: '',
  lastName: '',
  password: '',
  forcePasswordChange: false,
  roleId: '',
  department: '',
  additionalPermissions: '',
  providerId: '',
};

interface ProviderSummary {
  id: string;
  name: string;
  slug: string;
}

interface ProvidersListResponse {
  data: ProviderSummary[];
}

interface CreateProviderResponse {
  data: ProviderSummary;
}

interface NewProviderFormState {
  name: string;
  type: string;
  contactEmail: string;
}

const EMPTY_NEW_PROVIDER: NewProviderFormState = {
  name: '',
  type: 'EXTERNAL_API',
  contactEmail: '',
};

const CREATE_NEW_PROVIDER_VALUE = '__CREATE_NEW__';

/** Email check result — drives the two-mode create flow */
interface EmailCheckResult {
  exists: boolean;
  isAlreadyAdmin?: boolean;
  /**
   * True when the matched User row has a password hash. Only meaningful when
   * `exists === true`. When false, the create form must prompt for a password
   * so the newly-granted admin can actually log in.
   */
  hasPassword?: boolean;
  user?: { firstName: string | null; lastName: string | null; email: string };
}

/** Reset-password modal state */
interface ResetPasswordState {
  user: AdminUserSummary;
  password: string;
  forcePasswordChange: boolean;
}

const EMPTY_RESET_PASSWORD = (user: AdminUserSummary): ResetPasswordState => ({
  user,
  password: '',
  forcePasswordChange: false,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function displayName(user: AdminUserSummary): string {
  const parts = [user.firstName, user.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : user.email;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminUsersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // -- Local state ----------------------------------------------------------
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'suspended'>('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm] = useState<CreateFormState>(EMPTY_FORM);
  const [emailCheckResult, setEmailCheckResult] = useState<EmailCheckResult | null>(null);
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailCheckStep, setEmailCheckStep] = useState(true); // true = step 1 (email only)
  const [showNewProviderForm, setShowNewProviderForm] = useState(false);
  const [newProviderForm, setNewProviderForm] = useState<NewProviderFormState>(EMPTY_NEW_PROVIDER);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'suspend' | 'activate' | 'remove';
    user: AdminUserSummary;
  } | null>(null);
  const [resetPassword, setResetPassword] = useState<ResetPasswordState | null>(
    null,
  );

  // -- Queries --------------------------------------------------------------
  const usersQuery = useQuery<UsersListResponse>({
    queryKey: ['admin-users'],
    queryFn: () => api.get<UsersListResponse>('/api/v1/admin/users'),
  });

  const rolesQuery = useQuery<RolesListResponse>({
    queryKey: ['admin-roles'],
    queryFn: () => api.get<RolesListResponse>('/api/v1/admin/roles'),
  });

  const providersQuery = useQuery<ProvidersListResponse>({
    queryKey: ['providers-list'],
    queryFn: () => api.get<ProvidersListResponse>('/api/v1/admin/providers'),
  });

  // -- Mutations ------------------------------------------------------------
  const createMutation = useMutation({
    mutationFn: (body: CreateAdminUserRequest) =>
      api.post<{ data: AdminUserSummary }>('/api/v1/admin/users', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setShowCreateForm(false);
      setForm(EMPTY_FORM);
      setShowNewProviderForm(false);
      setNewProviderForm(EMPTY_NEW_PROVIDER);
    },
  });

  const createProviderMutation = useMutation({
    mutationFn: (body: { name: string; type: string; contactEmail?: string }) =>
      api.post<CreateProviderResponse>('/api/v1/admin/providers', body),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['providers-list'] });
      setForm((prev) => ({ ...prev, providerId: result.data.id }));
      setShowNewProviderForm(false);
      setNewProviderForm(EMPTY_NEW_PROVIDER);
    },
  });

  const suspendMutation = useMutation({
    mutationFn: (id: string) =>
      api.post<{ data: AdminUserSummary }>(`/api/v1/admin/users/${id}/suspend`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setConfirmAction(null);
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) =>
      api.post<{ data: AdminUserSummary }>(`/api/v1/admin/users/${id}/activate`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setConfirmAction(null);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) =>
      api.delete<{ message: string }>(`/api/v1/admin/users/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setConfirmAction(null);
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (body: {
      id: string;
      password: string;
      forcePasswordChange: boolean;
    }) =>
      api.post<{ message: string }>(
        `/api/v1/admin/users/${body.id}/password`,
        {
          password: body.password,
          forcePasswordChange: body.forcePasswordChange,
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setResetPassword(null);
    },
  });

  // -- Filtered data --------------------------------------------------------
  const users = usersQuery.data?.data ?? [];
  const roles = rolesQuery.data?.data ?? [];
  const providers = providersQuery.data?.data ?? [];

  const selectedRole = roles.find((r) => r.id === form.roleId);
  const isProviderRole = selectedRole?.slug === 'provider_ops';

  const filteredUsers = useMemo(() => {
    let result = users;

    // Search filter
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (u) =>
          u.email.toLowerCase().includes(q) ||
          (u.firstName?.toLowerCase().includes(q) ?? false) ||
          (u.lastName?.toLowerCase().includes(q) ?? false) ||
          u.role.name.toLowerCase().includes(q),
      );
    }

    // Role filter
    if (roleFilter) {
      result = result.filter((u) => u.role.id === roleFilter);
    }

    // Status filter
    if (statusFilter === 'active') {
      result = result.filter((u) => u.isActive);
    } else if (statusFilter === 'suspended') {
      result = result.filter((u) => !u.isActive);
    }

    return result;
  }, [users, search, roleFilter, statusFilter]);

  // -- Form handlers --------------------------------------------------------
  const updateField = useCallback(
    <K extends keyof CreateFormState>(key: K, value: CreateFormState[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  /**
   * Step 1: Check if the email belongs to an existing platform user.
   * If found, auto-fill name fields and show a user info card.
   * If not found, reveal name fields for manual input.
   */
  const handleEmailCheck = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!form.email.trim()) return;

      setEmailChecking(true);
      try {
        // Search platform users by exact email
        const result = await api.get<PlatformUsersResponse>(
          `/api/v1/admin/platform-users?search=${encodeURIComponent(form.email.trim())}&limit=5`,
        );

        const match = result.data.find(
          (u) => u.email.toLowerCase() === form.email.trim().toLowerCase(),
        );

        if (match) {
          setEmailCheckResult({
            exists: true,
            isAlreadyAdmin: !!match.isAdmin,
            // Default to `true` so old API responses (pre-hasPassword) don't
            // accidentally prompt for a reset; backend always returns the flag
            // now, so `false` only happens when the hash is genuinely null.
            hasPassword: match.hasPassword !== false,
            user: {
              firstName: match.firstName ?? null,
              lastName: match.lastName ?? null,
              email: match.email,
            },
          });
          // Auto-fill name fields from existing user
          setForm((prev) => ({
            ...prev,
            firstName: match.firstName ?? '',
            lastName: match.lastName ?? '',
          }));
        } else {
          setEmailCheckResult({ exists: false });
        }

        setEmailCheckStep(false);
      } catch {
        // If search fails, fall back to showing all fields
        setEmailCheckResult(null);
        setEmailCheckStep(false);
      } finally {
        setEmailChecking(false);
      }
    },
    [form.email],
  );

  const isNewUser = emailCheckResult !== null && !emailCheckResult.exists;
  /**
   * Existing user but with no password hash yet (e.g. OAuth-provisioned).
   * We must prompt for an initial password, otherwise the new admin can
   * never sign in to the admin panel.
   */
  const needsInitialPassword =
    emailCheckResult?.exists === true && emailCheckResult.hasPassword === false;
  const passwordRequired = isNewUser || needsInitialPassword;

  const handleCreateSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      const additionalPerms = form.additionalPermissions
        .split(',')
        .map((s: string) => s.trim())
        .filter(Boolean);

      createMutation.mutate({
        email: form.email,
        firstName: form.firstName,
        lastName: form.lastName,
        roleId: form.roleId,
        department: form.department || undefined,
        additionalPermissions: additionalPerms.length > 0 ? additionalPerms : undefined,
        providerId: isProviderRole && form.providerId ? form.providerId : undefined,
        // Password fields — required for brand-new users AND for existing
        // passwordless users (e.g. provisioned via Shopify OAuth) so they can
        // actually sign in to the admin panel.
        ...(passwordRequired && {
          password: form.password,
          forcePasswordChange: form.forcePasswordChange,
        }),
      });
    },
    [form, createMutation, passwordRequired, isProviderRole],
  );

  const cancelCreate = useCallback(() => {
    setShowCreateForm(false);
    setForm(EMPTY_FORM);
    setEmailCheckResult(null);
    setEmailCheckStep(true);
    setShowNewProviderForm(false);
    setNewProviderForm(EMPTY_NEW_PROVIDER);
    createMutation.reset();
  }, [createMutation]);

  const handleProviderSelectChange = useCallback((value: string) => {
    if (value === CREATE_NEW_PROVIDER_VALUE) {
      setShowNewProviderForm(true);
      setForm((prev) => ({ ...prev, providerId: '' }));
    } else {
      setShowNewProviderForm(false);
      setNewProviderForm(EMPTY_NEW_PROVIDER);
      setForm((prev) => ({ ...prev, providerId: value }));
    }
  }, []);

  const handleCreateProviderSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!newProviderForm.name.trim()) return;
      createProviderMutation.mutate({
        name: newProviderForm.name.trim(),
        type: newProviderForm.type,
        contactEmail: newProviderForm.contactEmail.trim() || undefined,
      });
    },
    [newProviderForm, createProviderMutation],
  );

  const handleConfirmAction = useCallback(() => {
    if (!confirmAction) return;
    const { type, user } = confirmAction;
    if (type === 'suspend') suspendMutation.mutate(user.id);
    else if (type === 'activate') activateMutation.mutate(user.id);
    else if (type === 'remove') removeMutation.mutate(user.id);
  }, [confirmAction, suspendMutation, activateMutation, removeMutation]);

  const isActionPending =
    suspendMutation.isPending || activateMutation.isPending || removeMutation.isPending;
  const actionError =
    suspendMutation.error?.message ??
    activateMutation.error?.message ??
    removeMutation.error?.message ??
    null;

  // -- Loading state --------------------------------------------------------
  if (usersQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" label="Loading admin users..." />
      </div>
    );
  }

  // -- Error state ----------------------------------------------------------
  if (usersQuery.isError) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <p className="text-sm text-red-400">
            Failed to load admin users:{' '}
            {usersQuery.error?.message ?? 'Unknown error'}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void usersQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  // -- Render ---------------------------------------------------------------
  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader
          title="Admin Users"
          description="Manage admin user accounts, roles, and access permissions."
          action={
            !showCreateForm ? (
              <Button size="sm" onClick={() => setShowCreateForm(true)}>
                + Create Admin User
              </Button>
            ) : undefined
          }
        />

        {/* Filters row */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="w-full max-w-xs">
            <Input
              placeholder="Search by name, email, or role..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
          >
            <option value="">All Roles</option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as '' | 'active' | 'suspended')
            }
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>

          {(search || roleFilter || statusFilter) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch('');
                setRoleFilter('');
                setStatusFilter('');
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>

        {/* Users table */}
        <div className="mt-4 overflow-x-auto rounded-lg border border-border">
          {filteredUsers.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-sm text-text-tertiary">
              {search || roleFilter || statusFilter
                ? 'No users match your filters.'
                : 'No admin users found. Create the first admin user to get started.'}
            </div>
          ) : (
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-surface-secondary">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Role
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Department
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Last Login
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-surface">
                {filteredUsers.map((user) => (
                  <tr
                    key={user.id}
                    className="transition-colors hover:bg-surface-secondary"
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => navigate(`/admin-users/${user.id}`)}
                        className="text-sm font-medium text-brand-400 hover:text-brand-300 hover:underline"
                      >
                        {displayName(user)}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                      {user.email}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge
                        variant={user.role.isSystemRole ? 'warning' : 'default'}
                        size="sm"
                      >
                        {user.role.name}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                      {user.department ?? '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {user.isActive ? (
                        <Badge variant="success" size="sm" dot>
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="danger" size="sm" dot>
                          Suspended
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-tertiary whitespace-nowrap">
                      {user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin-users/${user.id}`)}
                        >
                          View
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setResetPassword(EMPTY_RESET_PASSWORD(user))
                          }
                          className="text-blue-400 hover:text-blue-300"
                        >
                          Reset password
                        </Button>
                        {user.isActive ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setConfirmAction({ type: 'suspend', user })
                            }
                            className="text-amber-400 hover:text-amber-300"
                          >
                            Suspend
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setConfirmAction({ type: 'activate', user })
                            }
                            className="text-green-400 hover:text-green-300"
                          >
                            Activate
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setConfirmAction({ type: 'remove', user })
                          }
                          className="text-red-400 hover:text-red-300"
                        >
                          Remove
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination info */}
        {usersQuery.data?.pagination && (
          <div className="mt-3 text-xs text-text-tertiary">
            Showing {filteredUsers.length} of {usersQuery.data.pagination.total} admin
            users
          </div>
        )}
      </Card>

      {/* Create Admin User — two-step inline form */}
      {showCreateForm && (
        <Card>
          <CardHeader
            title="Create Admin User"
            description="Grant admin access to an existing platform user by email, or invite a new user."
          />

          {createMutation.isError && (
            <div className="mt-4 rounded-md bg-red-500/10 px-4 py-3 text-sm text-red-400">
              {createMutation.error?.message ?? 'Failed to create admin user.'}
            </div>
          )}

          {/* Step 1: Email check */}
          {emailCheckStep ? (
            <form onSubmit={handleEmailCheck} className="mt-4">
              <div className="max-w-md">
                <Input
                  label="Email Address"
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder="user@example.com"
                  required
                  autoFocus
                  hint="Enter the email to check if the user already exists on the platform."
                />
              </div>
              <div className="mt-4 flex items-center gap-3">
                <Button
                  type="submit"
                  disabled={!form.email.trim()}
                  loading={emailChecking}
                >
                  {emailChecking ? 'Checking...' : 'Check Email'}
                </Button>
                <Button type="button" variant="ghost" onClick={cancelCreate}>
                  Cancel
                </Button>
              </div>
            </form>
          ) : emailCheckResult?.isAlreadyAdmin ? (
            /* Already has admin access — block form */
            <div className="mt-4">
              <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-3">
                <p className="text-sm font-medium text-red-400">
                  Already has admin access
                </p>
                <p className="mt-1 text-sm text-text-secondary">
                  {emailCheckResult.user?.firstName}{' '}
                  {emailCheckResult.user?.lastName} ({emailCheckResult.user?.email})
                  already has admin access. Use the edit page to update their role or permissions.
                </p>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <Button type="button" variant="ghost" onClick={cancelCreate}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setEmailCheckStep(true);
                    setEmailCheckResult(null);
                    setForm(EMPTY_FORM);
                  }}
                >
                  Try Another Email
                </Button>
              </div>
            </div>
          ) : (
            /* Step 2: Full form (after email check) */
            <form onSubmit={handleCreateSubmit} className="mt-4">
              {/* User info card — existing user found (not admin) */}
              {emailCheckResult?.exists && emailCheckResult.user && (
                <div
                  className={`mb-4 rounded-lg border px-4 py-3 ${
                    needsInitialPassword
                      ? 'border-amber-500/30 bg-amber-500/5'
                      : 'border-green-500/30 bg-green-500/5'
                  }`}
                >
                  <p
                    className={`text-sm font-medium ${
                      needsInitialPassword ? 'text-amber-400' : 'text-green-400'
                    }`}
                  >
                    {needsInitialPassword
                      ? 'Existing user found — password required'
                      : 'Existing user found'}
                  </p>
                  <p className="mt-1 text-sm text-text-secondary">
                    {emailCheckResult.user.firstName}{' '}
                    {emailCheckResult.user.lastName} ({emailCheckResult.user.email})
                    {needsInitialPassword
                      ? // Passwordless user (e.g. created via Shopify OAuth). Without
                        // a password we cannot sign them into the admin panel.
                        ' — this user was created without a password (e.g. via Shopify login). Set an initial password below so they can sign in to the admin panel.'
                      : ' — this user will be granted admin access. Select a role below.'}
                  </p>
                </div>
              )}

              {/* New user notice */}
              {emailCheckResult && !emailCheckResult.exists && (
                <div className="mb-4 rounded-lg border border-yellow-500/30 bg-yellow-500/5 px-4 py-3">
                  <p className="text-sm font-medium text-yellow-400">
                    New user will be created
                  </p>
                  <p className="mt-1 text-sm text-text-secondary">
                    No existing user found for {form.email}. A new account will
                    be created with the password you set below.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {/* For existing users, show email as read-only summary */}
                {emailCheckResult?.exists && (
                  <div className="col-span-full">
                    <p className="text-sm text-text-secondary">
                      <span className="font-medium text-text-primary">Email:</span>{' '}
                      {form.email}
                    </p>
                  </div>
                )}

                {/* For new users only: name + email (password block below) */}
                {isNewUser && (
                  <>
                    <Input
                      label="Email"
                      type="email"
                      value={form.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      placeholder="user@example.com"
                      required
                      disabled
                    />
                    <Input
                      label="First Name"
                      value={form.firstName}
                      onChange={(e) => updateField('firstName', e.target.value)}
                      placeholder="John"
                      required
                    />
                    <Input
                      label="Last Name"
                      value={form.lastName}
                      onChange={(e) => updateField('lastName', e.target.value)}
                      placeholder="Doe"
                      required
                    />
                  </>
                )}

                {/* Password block — for brand-new users AND passwordless
                    existing users (e.g. provisioned via Shopify OAuth). */}
                {passwordRequired && (
                  <>
                    <div>
                      <Input
                        label={
                          needsInitialPassword
                            ? 'Initial Password'
                            : 'Password'
                        }
                        type="password"
                        value={form.password}
                        onChange={(e) => updateField('password', e.target.value)}
                        placeholder="Min 8 chars, upper/lower/digit/special"
                        required
                        hint="Must contain uppercase, lowercase, number, and special character"
                      />
                    </div>
                    <div className="flex items-center gap-2 self-end pb-2">
                      <input
                        id="forcePasswordChange"
                        type="checkbox"
                        checked={form.forcePasswordChange}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            forcePasswordChange: e.target.checked,
                          }))
                        }
                        className="h-4 w-4 rounded border-border bg-surface text-brand-500 focus:ring-brand-500/30"
                      />
                      <label
                        htmlFor="forcePasswordChange"
                        className="text-sm text-text-secondary"
                      >
                        Require password change on first login
                      </label>
                    </div>
                  </>
                )}

                {isNewUser && (
                  <div className="col-span-full">
                    <div className="rounded-lg border border-border/50 bg-surface-secondary/50 px-4 py-3 opacity-60">
                      <p className="text-sm font-medium text-text-tertiary">
                        Send Invite Link
                      </p>
                      <p className="mt-0.5 text-xs text-text-tertiary">
                        Let the user set their own password via email invite.
                        Available when email service is configured.
                      </p>
                    </div>
                  </div>
                )}

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-text-primary">
                    Role <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={form.roleId}
                    onChange={(e) => updateField('roleId', e.target.value)}
                    required
                    className="block w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
                  >
                    <option value="">Select a role...</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                        {role.isSystemRole ? ' (System)' : ''}
                      </option>
                    ))}
                  </select>
                  {rolesQuery.isLoading && (
                    <p className="mt-1 text-xs text-text-tertiary">
                      Loading roles...
                    </p>
                  )}
                </div>

                {isProviderRole && (
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-text-primary">
                      Provider <span className="text-red-400">*</span>
                    </label>
                    <select
                      value={showNewProviderForm ? CREATE_NEW_PROVIDER_VALUE : form.providerId}
                      onChange={(e) => handleProviderSelectChange(e.target.value)}
                      required={!showNewProviderForm}
                      className="block w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
                    >
                      <option value="">Select a provider...</option>
                      {providers.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                      <option value={CREATE_NEW_PROVIDER_VALUE} className="font-medium">
                        + Create New Provider
                      </option>
                    </select>
                    <p className="mt-1 text-xs text-text-tertiary">
                      This user will only see orders assigned to the selected provider.
                    </p>
                  </div>
                )}

                {/* Department & Permissions — only for new users; existing users can edit these later */}
                {isNewUser && (
                  <>
                    <Input
                      label="Department"
                      value={form.department}
                      onChange={(e) => updateField('department', e.target.value)}
                      placeholder="e.g. Engineering, Support"
                    />
                    <Input
                      label="Additional Permissions"
                      value={form.additionalPermissions}
                      onChange={(e) =>
                        updateField('additionalPermissions', e.target.value)
                      }
                      placeholder="permission.key, another.key"
                      hint="Comma-separated permission keys (optional)"
                    />
                  </>
                )}
              </div>

              {/* Inline Create Provider form */}
              {isProviderRole && showNewProviderForm && (
                <div className="mt-4 rounded-lg border border-brand-500/30 bg-brand-500/5 p-4">
                  <h4 className="text-sm font-semibold text-text-primary">Create New Provider</h4>
                  <p className="mt-1 text-xs text-text-secondary">
                    The provider will be created first, then automatically selected.
                  </p>

                  {createProviderMutation.isError && (
                    <div className="mt-3 rounded-md bg-red-500/10 px-4 py-3 text-sm text-red-400">
                      {createProviderMutation.error?.message ?? 'Failed to create provider.'}
                    </div>
                  )}

                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <Input
                      label="Provider Name"
                      value={newProviderForm.name}
                      onChange={(e) =>
                        setNewProviderForm((prev) => ({ ...prev, name: e.target.value }))
                      }
                      placeholder="e.g. Houston DTF"
                      required
                    />
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-text-primary">
                        Type
                      </label>
                      <select
                        value={newProviderForm.type}
                        onChange={(e) =>
                          setNewProviderForm((prev) => ({ ...prev, type: e.target.value }))
                        }
                        className="block w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
                      >
                        <option value="EXTERNAL_API">External API</option>
                        <option value="INTERNAL">Internal</option>
                        <option value="MANUAL">Manual</option>
                      </select>
                    </div>
                    <Input
                      label="Contact Email"
                      type="email"
                      value={newProviderForm.contactEmail}
                      onChange={(e) =>
                        setNewProviderForm((prev) => ({ ...prev, contactEmail: e.target.value }))
                      }
                      placeholder="provider@example.com"
                    />
                  </div>

                  <div className="mt-3 flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleCreateProviderSubmit}
                      disabled={!newProviderForm.name.trim()}
                      loading={createProviderMutation.isPending}
                    >
                      Create Provider
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowNewProviderForm(false);
                        setNewProviderForm(EMPTY_NEW_PROVIDER);
                      }}
                      disabled={createProviderMutation.isPending}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              <div className="mt-6 flex items-center justify-end gap-3 border-t border-border pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={cancelCreate}
                  disabled={createMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setEmailCheckStep(true);
                    setEmailCheckResult(null);
                    setForm(EMPTY_FORM);
                    setShowNewProviderForm(false);
                    setNewProviderForm(EMPTY_NEW_PROVIDER);
                  }}
                  disabled={createMutation.isPending}
                >
                  Change Email
                </Button>
                <Button
                  type="submit"
                  disabled={
                    !form.email.trim() ||
                    !form.firstName.trim() ||
                    !form.lastName.trim() ||
                    !form.roleId ||
                    (isProviderRole && !form.providerId) ||
                    (passwordRequired && !isPasswordValid(form.password))
                  }
                  loading={createMutation.isPending}
                >
                  {isNewUser
                    ? 'Create User & Grant Admin'
                    : needsInitialPassword
                      ? 'Set Password & Grant Admin'
                      : 'Grant Admin Access'}
                </Button>
              </div>
            </form>
          )}
        </Card>
      )}

      {/* Confirmation overlay */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="mx-4 w-full max-w-md rounded-xl border border-border bg-surface p-6 shadow-2xl">
            <h3 className="text-lg font-semibold text-text-primary">
              {confirmAction.type === 'suspend' && 'Suspend Admin User'}
              {confirmAction.type === 'activate' && 'Activate Admin User'}
              {confirmAction.type === 'remove' && 'Remove Admin Access'}
            </h3>
            <p className="mt-2 text-sm text-text-secondary">
              {confirmAction.type === 'suspend' && (
                <>
                  Are you sure you want to suspend{' '}
                  <strong className="text-text-primary">
                    {displayName(confirmAction.user)}
                  </strong>
                  ? They will lose access to the admin panel immediately.
                </>
              )}
              {confirmAction.type === 'activate' && (
                <>
                  Are you sure you want to reactivate{' '}
                  <strong className="text-text-primary">
                    {displayName(confirmAction.user)}
                  </strong>
                  ? They will regain admin panel access.
                </>
              )}
              {confirmAction.type === 'remove' && (
                <>
                  Are you sure you want to remove admin access for{' '}
                  <strong className="text-text-primary">
                    {displayName(confirmAction.user)}
                  </strong>
                  ? This action cannot be undone. The underlying user account will
                  not be deleted.
                </>
              )}
            </p>

            {actionError && (
              <div className="mt-3 rounded-md bg-red-500/10 px-4 py-3 text-sm text-red-400">
                {actionError}
              </div>
            )}

            <div className="mt-6 flex items-center justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setConfirmAction(null);
                  suspendMutation.reset();
                  activateMutation.reset();
                  removeMutation.reset();
                }}
                disabled={isActionPending}
              >
                Cancel
              </Button>
              <Button
                variant={confirmAction.type === 'remove' ? 'danger' : 'primary'}
                loading={isActionPending}
                onClick={handleConfirmAction}
              >
                {confirmAction.type === 'suspend' && 'Suspend User'}
                {confirmAction.type === 'activate' && 'Activate User'}
                {confirmAction.type === 'remove' && 'Remove Admin Access'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {resetPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <form
            className="mx-4 w-full max-w-md rounded-xl border border-border bg-surface p-6 shadow-2xl"
            onSubmit={(e) => {
              e.preventDefault();
              if (!isPasswordValid(resetPassword.password)) return;
              resetPasswordMutation.mutate({
                id: resetPassword.user.id,
                password: resetPassword.password,
                forcePasswordChange: resetPassword.forcePasswordChange,
              });
            }}
          >
            <h3 className="text-lg font-semibold text-text-primary">
              Reset password
            </h3>
            <p className="mt-2 text-sm text-text-secondary">
              Set a new password for{' '}
              <strong className="text-text-primary">
                {displayName(resetPassword.user)}
              </strong>{' '}
              ({resetPassword.user.email}). Their active sessions will be
              signed out immediately and they must use this new password to
              log back in.
            </p>

            <div className="mt-4">
              <Input
                label="New password"
                type="password"
                value={resetPassword.password}
                onChange={(e) =>
                  setResetPassword((prev) =>
                    prev ? { ...prev, password: e.target.value } : prev,
                  )
                }
                placeholder="Min 8 chars, upper/lower/digit/special"
                required
                autoFocus
                hint="Must contain uppercase, lowercase, number, and special character"
              />
            </div>

            <div className="mt-3 flex items-center gap-2">
              <input
                id="resetForcePasswordChange"
                type="checkbox"
                checked={resetPassword.forcePasswordChange}
                onChange={(e) =>
                  setResetPassword((prev) =>
                    prev
                      ? { ...prev, forcePasswordChange: e.target.checked }
                      : prev,
                  )
                }
                className="h-4 w-4 rounded border-border bg-surface text-brand-500 focus:ring-brand-500/30"
              />
              <label
                htmlFor="resetForcePasswordChange"
                className="text-sm text-text-secondary"
              >
                Require password change on next login
              </label>
            </div>

            {resetPasswordMutation.isError && (
              <div className="mt-3 rounded-md bg-red-500/10 px-4 py-3 text-sm text-red-400">
                {resetPasswordMutation.error?.message ??
                  'Failed to reset password.'}
              </div>
            )}

            {resetPasswordMutation.isSuccess && (
              <div className="mt-3 rounded-md bg-green-500/10 px-4 py-3 text-sm text-green-400">
                Password reset successfully.
              </div>
            )}

            <div className="mt-6 flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setResetPassword(null);
                  resetPasswordMutation.reset();
                }}
                disabled={resetPasswordMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={resetPasswordMutation.isPending}
                disabled={!isPasswordValid(resetPassword.password)}
              >
                Reset password
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
